import { useRef, useState } from 'react'
import { Coche } from './Icone.tsx'
import type { EtatStock } from '../lib/ops/index.ts'

/**
 * LA JAUGE D'ÉTAT — le contrôle le plus utilisé de l'application.
 *
 * POURQUOI TAP-TO-CYCLE, ET PAS AUTRE CHOSE
 *
 * Un segmented control à trois positions visibles serait parfaitement
 * découvrable, mais il prend ~130 px de large × 200 lignes : l'inventaire
 * deviendrait un écran de réglages et « sobre » mourrait. Un appui long
 * ajoute 400 ms de latence sur l'action la plus fréquente de l'app. Un swipe
 * entre en conflit avec le scroll, et un geste par article est plus lent
 * qu'un tap.
 *
 * Reste le tap-to-cycle, dont le seul défaut est la découvrabilité — et
 * c'est le seul défaut RÉPARABLE. On le répare par la FORME, pas par un
 * tutoriel : ce n'est pas une pastille opaque, c'est un NIVEAU. Trois barres
 * qui se vident. « Ça descend » se lit sans mode d'emploi, l'état reste
 * lisible SANS COULEUR (on compte les barres — le daltonisme est réglé au
 * passage), et ça ne coûte que 15 px de large.
 *
 * Le cycle suit la dépletion physique : en stock → bientôt fini → épuisé.
 * C'est le sens dans lequel la nourriture va réellement. Le retour en
 * arrière est l'événement rare, et il se fait normalement en cochant en mode
 * magasin, qui remet l'article en stock : la boucle se referme sans jamais
 * demander de remonter le cycle à la main.
 */

const SUIVANT: Record<EtatStock, EtatStock> = {
  en_stock: 'bientot_fini',
  bientot_fini: 'epuise',
  epuise: 'en_stock',
}

export const LIBELLE: Record<EtatStock, string> = {
  en_stock: 'en stock',
  bientot_fini: 'bientôt fini',
  epuise: 'épuisé',
}

/** Barres pleines par état. Trois valeurs, trois hauteurs de niveau. */
const PLEINES: Record<EtatStock, number> = { en_stock: 3, bientot_fini: 1, epuise: 0 }

export function StateGauge({
  etat,
  onChange,
  label,
}: {
  etat: EtatStock
  onChange: (suivant: EtatStock) => void
  label: string
}) {
  const [menu, setMenu] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPress = useRef(false)

  const pleines = PLEINES[etat]

  function debut() {
    longPress.current = false
    timer.current = setTimeout(() => {
      longPress.current = true
      setMenu(true)
      // Sur iPhone c'est inopérant (Safari n'implémente pas vibrate) : c'est
      // un bonus Android, jamais une information nécessaire.
      navigator.vibrate?.(12)
    }, 400)
  }

  function fin() {
    if (timer.current) clearTimeout(timer.current)
    if (longPress.current) return
    onChange(SUIVANT[etat])
    navigator.vibrate?.(etat === 'bientot_fini' ? [0, 10, 40, 16] : 8)
  }

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={`${label} — ${LIBELLE[etat]}. Toucher pour changer.`}
        data-etat={etat}
        onPointerDown={debut}
        onPointerUp={fin}
        onPointerLeave={() => timer.current && clearTimeout(timer.current)}
        onContextMenu={(e) => e.preventDefault()}
        /* touch-callout coupé : l'appui long est lié au menu d'états et se
           battrait sinon avec le menu système d'iOS. */
        className="pressable flex size-11 items-center justify-center [-webkit-touch-callout:none]"
      >
        <span className="flex h-4 items-end gap-[3px]">
          {[0, 1, 2].map((i) => {
            /*
             * Chaque barre est une PISTE toujours visible, dans laquelle un
             * remplissage descend. C'est ce qui fait lire un NIVEAU.
             *
             * La première version ne rendait que les barres pleines, en
             * écrasant les autres à 18 % de hauteur : à l'état « épuisé » les
             * trois moignons se lisaient comme des points de suspension, pas
             * comme un réservoir vide. La piste résout ça — vide, elle reste
             * une piste.
             *
             * Le remplissage monte du BAS (origin-bottom) parce que c'est le
             * sens dans lequel un niveau se vide.
             */
            const pleine = i < pleines
            return (
              <span
                key={i}
                className="relative w-[3px] overflow-hidden rounded-full bg-line"
                style={{ height: 16 }}
              >
                <span
                  className="gauge-bar absolute inset-0 origin-bottom rounded-full transition-[transform,background-color] duration-[140ms] ease-(--ease-out)"
                  style={{
                    transform: pleine ? 'scaleY(1)' : 'scaleY(0)',
                    backgroundColor:
                      etat === 'en_stock'
                        ? 'var(--color-ink-soft)' /* aucune couleur : c'est la normale */
                        : 'var(--color-bientot)',
                  }}
                />
              </span>
            )
          })}
        </span>
      </button>

      {menu && (
        <>
          <button
            type="button"
            aria-label="Fermer"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setMenu(false)}
          />
          {/* transform-origin AU DÉCLENCHEUR, pas au centre : le popover doit
              paraître sortir de la jauge qu'on vient de presser. */}
          <div
            role="menu"
            className="absolute right-0 top-11 z-50 w-44 origin-top-right rounded-md bg-surface p-1 shadow-pop"
            style={{ animation: 'pop 180ms var(--ease-out)' }}
          >
            {(Object.keys(LIBELLE) as EtatStock[]).map((e) => (
              <button
                key={e}
                type="button"
                role="menuitemradio"
                aria-checked={e === etat}
                onClick={() => {
                  setMenu(false)
                  if (e !== etat) onChange(e)
                }}
                className="pressable flex h-11 w-full items-center justify-between rounded-sm px-3 text-left text-body"
              >
                <span className={e === etat ? 'font-semibold' : ''}>{LIBELLE[e]}</span>
                {e === etat && <Coche className="size-4 text-accent" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
